import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { CircleCheck, Clock3, RotateCw } from 'lucide-react';
import { TemplateString } from '@/components/template-string';
import { toast } from '@/components/ui/sonner';
import { useIsAuthenticated } from '@/hooks/use-is-authenticated';
import { useI18nContext } from '@/i18n/i18n-react';
import { logIn } from '@/lib/api-client';
import { useLeaveAuth } from '@/pages/auth/auth-exit';
import { AuthPageLayout } from '@/pages/auth/auth-page-layout';
import {
  RESEND_COOLDOWN_S,
  formatCountdown,
  useCountdown,
} from '@/pages/auth/use-countdown';
import { webmailFor } from '@/pages/auth/webmail';

/** How often the tab checks whether the link was opened (same browser). */
const SESSION_POLL_MS = 3000;

// "Confira seu e-mail", inside the auth frame (AuthRouteTransition): the link
// was just sent. It offers the merchant's webmail, a resend once the
// cooldown is over, and moves on to the dashboard by itself as soon as the
// link opens a session in another tab (the session cookie is shared).
export default function LoginSuccessPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  if (!email) return <Navigate to="/login" replace />;
  return <CheckEmail email={email} />;
}

function CheckEmail({ email }: { email: string }) {
  const { LL } = useI18nContext();
  const leave = useLeaveAuth();
  const reduceMotion = useReducedMotion();
  const session = useIsAuthenticated({ pollMs: SESSION_POLL_MS });
  const cooldown = useCountdown(RESEND_COOLDOWN_S);
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const webmail = webmailFor(email);

  useEffect(() => {
    if (session.data === true) leave('/select-tenant');
  }, [session.data, leave]);

  const resend = async () => {
    setSending(true);
    setResent(false);
    try {
      await logIn(email);
      setResent(true);
      cooldown.restart();
    } catch {
      toast.error(LL.loginSuccess.resendFailed());
    } finally {
      setSending(false);
    }
  };

  const waiting = cooldown.left > 0;

  return (
    <AuthPageLayout>
      <AuthPageLayout.Header
        title={LL.loginSuccess.title()}
        subtitle={
          <TemplateString
            templateString={LL.loginSuccess.sentTo()}
            componentMap={{
              Email: () => (
                <strong className="font-semibold text-[#15191E] [overflow-wrap:anywhere]">
                  {email}
                </strong>
              ),
              Cta: () => (
                <strong className="font-semibold text-[#15191E]">
                  {LL.loginSuccess.ctaName()}
                </strong>
              ),
            }}
          />
        }
      />
      <AuthPageLayout.Content className="gap-3">
        {webmail && (
          <AuthPageLayout.PrimaryLink
            href={webmail.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {webmail.provider === 'gmail'
              ? LL.loginSuccess.openGmail()
              : LL.loginSuccess.openOutlook()}
          </AuthPageLayout.PrimaryLink>
        )}
        <AuthPageLayout.Secondary
          disabled={waiting || sending}
          onClick={() => void resend()}
        >
          <RotateCw aria-hidden strokeWidth={1.5} className="size-[18px]" />
          {sending
            ? LL.loginSuccess.resending()
            : waiting
              ? LL.loginSuccess.resendIn({
                  time: formatCountdown(cooldown.left),
                })
              : LL.loginSuccess.resend()}
        </AuthPageLayout.Secondary>
        <p
          role="status"
          className="flex min-h-0 items-center gap-2 text-sm text-[#15191E] empty:hidden"
        >
          {resent && (
            <>
              <CircleCheck
                aria-hidden
                strokeWidth={1.5}
                className="size-[18px] shrink-0 text-[#155DFC]"
              />
              {LL.loginSuccess.resent()}
            </>
          )}
        </p>
      </AuthPageLayout.Content>

      <div className="mt-9 flex flex-col gap-3.5 border-t border-[#15191E]/[0.08] pt-5 text-sm leading-normal text-[#4B5361]">
        <p className="flex items-start gap-2.5">
          <motion.span
            aria-hidden
            className="mx-[5px] mt-1.5 size-2 shrink-0 rounded-full bg-[#155DFC]"
            animate={
              reduceMotion
                ? undefined
                : {
                    boxShadow: [
                      '0 0 0 0 rgba(21,93,252,0.45)',
                      '0 0 0 6px rgba(21,93,252,0)',
                      '0 0 0 0 rgba(21,93,252,0)',
                    ],
                  }
            }
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
          />
          <span>{LL.loginSuccess.waiting()}</span>
        </p>
        <p className="flex items-start gap-2.5">
          <Clock3
            aria-hidden
            strokeWidth={1.5}
            className="mt-px size-[18px] shrink-0"
          />
          <span>{LL.loginSuccess.notArrived()}</span>
        </p>
      </div>

      <AuthPageLayout.Footer>
        <p className="text-sm text-[#4B5361]">
          {LL.loginSuccess.wrongEmail()}{' '}
          <Link
            to="/login"
            className="font-semibold text-[#155DFC] hover:underline"
          >
            {LL.loginSuccess.backToLogin()}
          </Link>
        </p>
      </AuthPageLayout.Footer>
    </AuthPageLayout>
  );
}
